import { Amplify, Auth } from "aws-amplify";
import { CognitoUser } from "amazon-cognito-identity-js";

import { getSiteKeys } from "../../utils/config";
import EcommerceApi from "../../services/ecommerceApi";
import { CustomerType, ICustomer } from "../../types";

let cognitoUser: CognitoUser | undefined = undefined;

Amplify.configure({
    Auth: {
        region: process.env.GATSBY_REGION,
        userPoolId: process.env.GATSBY_USER_POOL_ID,
        userPoolWebClientId: process.env.GATSBY_USER_POOL_WEB_CLIENT_ID
    }
});

const normalizeUser = user => {
    if (user) {
        for (const key in user.attributes) {
            const attr = user.attributes[key];
            user[key] = attr;
        }
    }
    return user;
};

// const signIn = async (username, password) => {
//     try {
//         const user = await Auth.signIn(username, password);
//         return user;
//     } catch (e) {
//         console.error("signIn", e);
//     }
//     return null;
// };

const signOut = async () => {
    try {
        return await Auth.signOut();
    } catch (e) {
        console.error("signOut", e);
    }
    return null;
};

const getCognitoUser = async () => {
    try {
        return normalizeUser(await Auth.currentAuthenticatedUser());
    } catch (e) {
        return null;
    }
};
function getRandomString(bytes: number) {
    const randomValues = new Uint8Array(bytes);
    window.crypto.getRandomValues(randomValues);
    return Array.from(randomValues).map(intToHex).join("");
}

function intToHex(nr: number) {
    return nr.toString(16).padStart(2, "0");
}
export async function signUp(customer: ICustomer) {
    const { tenant, salesChannel: scid } = getSiteKeys();

    if (!customer) {
        throw new Error("Customer not found");
    }

    const params = {
        username: `${scid}-${customer?.id}`,
        password: `D3!g${getRandomString(30).toString()}`,
        attributes: {
            given_name: customer.firstName,
            family_name: customer.lastName,
            email: customer.email,
            "custom:customer_id": customer.id,
            "custom:tenant_id": tenant,
            "custom:sales_channel": scid
        }
    };

    return await Auth.signUp(params);
}

export async function getCustomer(email: string): Promise<CustomerType | undefined | "apiError"> {
    const customerLookup = await EcommerceApi.getCustomerByEmail(email);

    if (!customerLookup) {
        return undefined;
    }

    if (customerLookup === "error") {
        return "apiError";
    }

    if (customerLookup.length < 1) {
        return undefined;
    }

    return customerLookup.find(c => c.email.toLowerCase() === email.toLowerCase()) || undefined;
}

type SignInResponse = {
    user?: CognitoUser;
    customer?: CustomerType;
    apiError?: boolean;
};
export async function signIn(email: string): Promise<SignInResponse> {
    const { salesChannel } = getSiteKeys();

    const customer = await getCustomer(email);

    if (!customer) {
        return {};
    }

    if (customer === "apiError") {
        console.error("CognitoService", "signIn", "API error while fetching customer");
        return {
            apiError: true
        };
    }

    cognitoUser = undefined;
    try {
        cognitoUser = await Auth.signIn(`${salesChannel}-${customer.id}`);
    } catch (e) {
        const eName = e?.name || "UnknownError";
        console.error("CognitoService", "signIn", eName, e.message, e);
        const identityError = e?.message?.includes("The following identities");
        if (identityError) {
            return {
                apiError: true,
                customer
            };
        }
        return {
            user: undefined,
            customer: customer
        };
    }

    return {
        user: cognitoUser,
        customer
    };
}
export async function answerCustomChallenge(answer: string) {
    cognitoUser = await Auth.sendCustomChallengeAnswer(cognitoUser, answer);
    try {
        // This will throw an error if the user is not yet authenticated:
        await Auth.currentSession();
        return true;
    } catch {
        return false;
    }
}
// get auth token
const getAuthToken = async () => {
    try {
        const session = await Auth.currentSession();

        return session.getIdToken().getJwtToken();
    } catch (e) {
        console.error("getAuthToken", e);
        return null;
    }
};

const CognitoService = {
    signIn,
    signUp,
    signOut,
    getCognitoUser,
    getAuthToken,
    getCustomer
};

export default CognitoService;
